/**
 * Switch Component Handler
 * Handles custom switch UI interactions for privacy settings
 * 
 * @author InterPals Development Team
 * @version 1.0.0
 */

(function() {
    'use strict';

    // Constants
    const CSS_CLASSES = {
        SWITCH_INPUT: 'switch-input',
        SWITCH_BACKGROUND_ACTIVE: 'bg-brand',
        SWITCH_BACKGROUND_INACTIVE: 'bg-gray-200',
        SWITCH_THUMB_ACTIVE: 'translate-x-5',
        SWITCH_THUMB_INACTIVE: 'translate-x-0.5'
    };

    const ARIA_ATTRIBUTES = {
        TABINDEX: 'tabindex',
        ROLE: 'role',
        ARIA_CHECKED: 'aria-checked'
    };

    const KEYBOARD_KEYS = {
        SPACE: ' ',
        ENTER: 'Enter'
    };

    /**
     * Initialize switch components
     * Finds all switch inputs and sets up event listeners
     */
    function initSwitchComponents() {
        // Find all switch inputs and work up to their containers
        const switchInputs = document.querySelectorAll('.' + CSS_CLASSES.SWITCH_INPUT);
        
        switchInputs.forEach(function(input) {
            const container = input.closest('label');
            if (!container) return;
            
            const switchUI = container.querySelector('.switch-ui, .relative.inline-flex');
            
            if (!switchUI) return;
            
            // Ensure visual state matches input state on init
            updateSwitchVisuals(input, switchUI);
            
            // Add click handler to the container (label) for AJAX switches
            if (input.classList.contains('ajax-privacy-switch')) {
                // Prevent the default label behavior and handle the toggle manually
                container.addEventListener('click', function(e) {
                    e.preventDefault();
                    toggleSwitch(input, switchUI);
                });
            } else {
                // Make the switch UI clickable for non-AJAX switches
                switchUI.addEventListener('click', function(e) {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleSwitch(input, switchUI);
                });
            }
            
            // Handle keyboard interaction
            switchUI.addEventListener('keydown', function(e) {
                if (e.key === KEYBOARD_KEYS.SPACE || e.key === KEYBOARD_KEYS.ENTER) {
                    e.preventDefault();
                    toggleSwitch(input, switchUI);
                }
            });
            
            // Listen for change events on the input to update visuals
            input.addEventListener('change', function() {
                updateSwitchVisuals(input, switchUI);
            });
            
            // Make switch UI focusable and accessible
            switchUI.setAttribute(ARIA_ATTRIBUTES.TABINDEX, '0');
            switchUI.setAttribute(ARIA_ATTRIBUTES.ROLE, 'switch');
            switchUI.setAttribute(ARIA_ATTRIBUTES.ARIA_CHECKED, input.checked.toString());
        });
    }

    /**
     * Toggle switch state
     * @param {HTMLInputElement} input - The hidden checkbox input
     * @param {HTMLElement} switchUI - The visual switch element
     */
    function toggleSwitch(input, switchUI) {
        // Toggle the input state
        input.checked = !input.checked;
        
        // Update visual state
        updateSwitchVisuals(input, switchUI);
        
        // Trigger change event for form handling
        const changeEvent = new Event('change', { bubbles: true });
        input.dispatchEvent(changeEvent);
    }

    /**
     * Update switch visual state based on input state
     * @param {HTMLInputElement} input - The hidden checkbox input
     * @param {HTMLElement} switchUI - The visual switch element
     */
    function updateSwitchVisuals(input, switchUI) {
        const isChecked = input.checked;
        const thumb = switchUI.querySelector('span');
        
        // Update switch background
        if (isChecked) {
            switchUI.classList.remove(CSS_CLASSES.SWITCH_BACKGROUND_INACTIVE);
            switchUI.classList.add(CSS_CLASSES.SWITCH_BACKGROUND_ACTIVE);
        } else {
            switchUI.classList.remove(CSS_CLASSES.SWITCH_BACKGROUND_ACTIVE);
            switchUI.classList.add(CSS_CLASSES.SWITCH_BACKGROUND_INACTIVE);
        }
        
        // Update thumb position
        if (thumb) {
            if (isChecked) {
                thumb.classList.remove(CSS_CLASSES.SWITCH_THUMB_INACTIVE);
                thumb.classList.add(CSS_CLASSES.SWITCH_THUMB_ACTIVE);
            } else {
                thumb.classList.remove(CSS_CLASSES.SWITCH_THUMB_ACTIVE);
                thumb.classList.add(CSS_CLASSES.SWITCH_THUMB_INACTIVE);
            }
        }
        
        // Update ARIA state for accessibility
        switchUI.setAttribute(ARIA_ATTRIBUTES.ARIA_CHECKED, isChecked.toString());
    }

    /**
     * Resync a switch's visuals with its input.
     *
     * Assigning `input.checked` fires no `change` event, so a state set in code
     * rather than by the user leaves the track and thumb showing the old value.
     * Dispatching `change` instead would re-enter the ajax handlers bound to
     * that same event and start a second save.
     *
     * @param {HTMLInputElement} input - The hidden checkbox input
     */
    function refreshSwitch(input) {
        const container = input.closest('label');
        const switchUI = container ? container.querySelector('.switch-ui, .relative.inline-flex') : null;

        if (switchUI) {
            updateSwitchVisuals(input, switchUI);
        }
    }

    window.SwitchComponent = {
        refresh: refreshSwitch
    };

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initSwitchComponents);
    } else {
        initSwitchComponents();
    }

    // Re-initialize if new content is added dynamically
    document.addEventListener('contentUpdated', initSwitchComponents);

})(); 